/**
 * 小红书爆款 - 快速冲击模板
 *
 * 视觉结构：
 * - 开场钩子（2秒）：故障风文字冲击 + 多层霓虹
 * - 主体内容（15秒）：高密度信息卡片 + 进度指示器
 * - 结尾CTA（3秒）：总结 + 脉冲光环CTA
 *
 * 视觉元素：
 * - 配色：高对比（深色底 + 霓虹色）
 * - 字号：120-200px（开场）、36-52px（卡片）
 * - 动画：弹跳冲击、故障闪烁、扫描线
 * - 背景：动态粒子 + 扫描线 + 渐变光晕
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
} from 'remotion';

const FAST_COLORS = {
  primary: {
    neonPink: '#FF006E',
    neonBlue: '#00F5FF',
    neonYellow: '#FFE942',
    neonGreen: '#39FF14',
    neonPurple: '#BF00FF',
  },
  secondary: {
    red: '#FF2D55',
    orange: '#FF6600',
    cyan: '#00FFFF',
    magenta: '#FF00FF',
  },
  gradients: {
    neon: 'linear-gradient(135deg, #FF006E 0%, #00F5FF 50%, #FFE942 100%)',
    dark: 'linear-gradient(180deg, #05060F 0%, #0D0F1A 50%, #111328 100%)',
    cyber: 'linear-gradient(135deg, #BF00FF 0%, #00F5FF 100%)',
    pinkBlue: 'linear-gradient(135deg, #FF006E 0%, #00F5FF 100%)',
  },
  white: '#FFFFFF',
  black: '#000000',
  lightGray: '#B8C0D4',
  darkSurface: 'rgba(255, 255, 255, 0.06)',
};

// ============================================
// ⚡ 动态粒子 + 扫描线背景
// ============================================
const DynamicParticleBackground: React.FC<{
  count?: number;
}> = ({ count = 50 }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const particles = Array.from({ length: count }, (_, i) => {
    const x = (i * 27.137) % width;
    const baseY = (i * 33.723) % height;
    const speed = 2 + (i % 4) * 1.5;
    const y = (baseY + frame * speed) % height;
    const size = 2 + (i % 4) * 2;
    const opacity = 0.25 + Math.sin(frame * 0.08 + i) * 0.15;
    const colors = [
      FAST_COLORS.primary.neonPink,
      FAST_COLORS.primary.neonBlue,
      FAST_COLORS.primary.neonYellow,
      FAST_COLORS.primary.neonGreen,
      FAST_COLORS.primary.neonPurple,
    ];
    return { x, y, size, opacity, color: colors[i % colors.length] };
  });

  const scanY = (frame * 4) % height;

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: FAST_COLORS.gradients.dark,
        }}
      />

      {/* 角落渐变光晕 */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${FAST_COLORS.primary.neonPink}18 0%, transparent 70%)`,
          filter: 'blur(60px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -200,
          left: -200,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${FAST_COLORS.primary.neonBlue}18 0%, transparent 70%)`,
          filter: 'blur(60px)',
        }}
      />

      {/* 扫描线 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: scanY,
          width: '100%',
          height: 2,
          background: `linear-gradient(90deg, transparent 0%, ${FAST_COLORS.primary.neonBlue}60 50%, transparent 100%)`,
          boxShadow: `0 0 20px ${FAST_COLORS.primary.neonBlue}40`,
        }}
      />

      {/* 粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 开场钩子 - 故障风冲击（2秒）
// ============================================
const FastHookScene: React.FC<{
  texts: string[];
}> = ({ texts }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const totalFrames = fps * 2;
  const framesPerText = Math.floor(totalFrames / texts.length);
  const currentIndex = Math.min(
    Math.floor(frame / framesPerText),
    texts.length - 1,
  );
  const text = texts[currentIndex];
  const localFrame = frame - currentIndex * framesPerText;

  const textProgress = spring({
    frame: localFrame,
    fps,
    config: { damping: 8, stiffness: 300, mass: 0.3 },
  });

  const scale = interpolate(textProgress, [0, 1], [0.3, 1]);
  const rotate = interpolate(textProgress, [0, 1], [-8, 0]);

  const colors = [
    FAST_COLORS.primary.neonPink,
    FAST_COLORS.primary.neonBlue,
    FAST_COLORS.primary.neonYellow,
  ];
  const currentColor = colors[currentIndex % colors.length];

  const glitchOffset =
    localFrame < 8 ? Math.sin(localFrame * 5) * 6 : 0;

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 64,
      }}
    >
      {/* 故障层-偏移复制 */}
      {localFrame < 10 && (
        <>
          <div
            style={{
              position: 'absolute',
              fontSize: 200,
              fontWeight: 900,
              color: FAST_COLORS.primary.neonPink,
              textAlign: 'center',
              lineHeight: 1,
              transform: `scale(${scale}) rotate(${rotate}deg) translate(${glitchOffset}px, ${-glitchOffset * 0.5}px)`,
              opacity: 0.4,
              mixBlendMode: 'screen',
            }}
          >
            {text}
          </div>
          <div
            style={{
              position: 'absolute',
              fontSize: 200,
              fontWeight: 900,
              color: FAST_COLORS.primary.neonBlue,
              textAlign: 'center',
              lineHeight: 1,
              transform: `scale(${scale}) rotate(${rotate}deg) translate(${-glitchOffset}px, ${glitchOffset * 0.5}px)`,
              opacity: 0.4,
              mixBlendMode: 'screen',
            }}
          >
            {text}
          </div>
        </>
      )}

      {/* 主文字 */}
      <div
        style={{
          fontSize: 200,
          fontWeight: 900,
          color: currentColor,
          textAlign: 'center',
          lineHeight: 1,
          transform: `scale(${scale}) rotate(${rotate}deg)`,
          textShadow: `
            0 0 20px ${currentColor},
            0 0 60px ${currentColor}80,
            0 0 100px ${currentColor}40
          `,
        }}
      >
        {text}
      </div>

      {/* 底部装饰线 */}
      <div
        style={{
          position: 'absolute',
          bottom: 300,
          width: interpolate(textProgress, [0, 1], [0, 400]),
          height: 4,
          background: FAST_COLORS.gradients.pinkBlue,
          borderRadius: 2,
          boxShadow: `0 0 16px ${currentColor}60`,
        }}
      />
    </AbsoluteFill>
  );
};

// ============================================
// 📊 高密度信息卡片
// ============================================
const InfoCard: React.FC<{
  number: string;
  title: string;
  content: string;
  color: string;
  delay: number;
  index: number;
  total: number;
}> = ({ number, title, content, color, delay, index, total }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 180, mass: 0.5 },
  });

  const slideX = interpolate(progress, [0, 1], [400, 0]);
  const scale = interpolate(progress, [0, 1], [0.85, 1]);

  const shimmer =
    0.06 + Math.sin(frame * 0.06 + index * 1.2) * 0.03;

  return (
    <div
      style={{
        position: 'relative',
        background: `linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)`,
        backdropFilter: 'blur(20px)',
        borderRadius: 20,
        padding: '20px 28px',
        border: `2px solid ${color}50`,
        borderLeft: `5px solid ${color}`,
        opacity: progress,
        transform: `translateX(${slideX}px) scale(${scale})`,
        boxShadow: `
          0 4px 24px ${color}20,
          inset 0 0 40px rgba(255,255,255,${shimmer})
        `,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        overflow: 'hidden',
        marginBottom: 20
      }}
    >
      {/* 编号圆 */}
      <div
        style={{
          minWidth: 64,
          height: 64,
          borderRadius: 16,
          background: `linear-gradient(135deg, ${color} 0%, ${color}BB 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 44,
          fontWeight: 900,
          color: FAST_COLORS.black,
          boxShadow: `0 4px 20px ${color}50`,
          marginRight: 20,
        }}
      >
        {number}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: FAST_COLORS.white,
            marginBottom: 15,
            letterSpacing: 0.5,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 36,
            fontWeight: 500,
            color: FAST_COLORS.lightGray,
            lineHeight: 1.4,
          }}
        >
          {content}
        </div>
      </div>

      {/* 进度指示 */}
      <div
        style={{
          fontSize: 40,
          fontWeight: 700,
          color: `${color}90`,
          whiteSpace: 'nowrap',
        }}
      >
        {index + 1}/{total}
      </div>
    </div>
  );
};

// ============================================
// 🎬 主体信息场景（15秒）
// ============================================
const FastInfoScene: React.FC<{
  items: Array<{
    number: string;
    title: string;
    content: string;
    color: string;
  }>;
  sceneTitle?: string;
}> = ({ items, sceneTitle }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const lineWidth = interpolate(titleProgress, [0, 1], [0, 200]);

  return (
    <AbsoluteFill>
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 48,
          right: 48,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 60,
            fontWeight: 800,
            background: FAST_COLORS.gradients.neon,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            opacity: titleProgress,
            transform: `scale(${interpolate(titleProgress, [0, 1], [0.85, 1])})`,
            letterSpacing: 2,
          }}
        >
          {sceneTitle || `⚡ ${items.length} 个关键点`}
        </div>
        {/* 标题下划线 */}
        <div
          style={{
            margin: '16px auto 0',
            width: lineWidth,
            height: 3,
            background: FAST_COLORS.gradients.pinkBlue,
            borderRadius: 2,
          }}
        />
      </div>

      {/* 信息卡片 */}
      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 40,
          right: 40,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {items.map((item, i) => (
          <InfoCard
            key={i}
            {...item}
            delay={12 + i * 25}
            index={i}
            total={items.length}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 结尾CTA场景（3秒）
// ============================================
const FastCTAScene: React.FC<{
  summary: string;
  ctaText: string;
}> = ({ summary, ctaText }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame,
    fps,
    config: { damping: 8, stiffness: 250, mass: 0.4 },
  });

  const scale = interpolate(progress, [0, 1], [0.4, 1]);
  const pulse = 1 + Math.sin(frame * 0.25) * 0.04;

  const ringScale = 1 + Math.sin(frame * 0.15) * 0.08;
  const ringOpacity = 0.15 + Math.sin(frame * 0.15) * 0.1;

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 脉冲光环 */}
      <div
        style={{
          position: 'absolute',
          width: 600,
          height: 600,
          borderRadius: '50%',
          border: `3px solid ${FAST_COLORS.primary.neonPink}`,
          opacity: ringOpacity,
          transform: `scale(${ringScale})`,
          boxShadow: `0 0 40px ${FAST_COLORS.primary.neonPink}30`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 700,
          height: 700,
          borderRadius: '50%',
          border: `2px solid ${FAST_COLORS.primary.neonBlue}`,
          opacity: ringOpacity * 0.6,
          transform: `scale(${ringScale * 0.95})`,
        }}
      />

      {/* 总结文字 */}
      <div
        style={{
          fontSize: 96,
          fontWeight: 900,
          background: FAST_COLORS.gradients.neon,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          transform: `scale(${scale * pulse})`,
          marginBottom: 60,
          letterSpacing: 3,
        }}
      >
        {summary}
      </div>

      {/* CTA 按钮 */}
      <div
        style={{
          background: FAST_COLORS.gradients.neon,
          padding: '28px 72px',
          borderRadius: 80,
          fontSize: 52,
          fontWeight: 900,
          color: FAST_COLORS.black,
          opacity: progress,
          transform: `scale(${interpolate(progress, [0, 1], [0.6, 1]) * pulse})`,
          boxShadow: `
            0 0 30px ${FAST_COLORS.primary.neonPink}50,
            0 0 60px ${FAST_COLORS.primary.neonBlue}30,
            inset 0 0 20px rgba(255,255,255,0.15)
          `,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          letterSpacing: 2,
        }}
      >
        <span>⚡</span>
        <span>{ctaText}</span>
      </div>

      {/* 底部装饰 */}
      <div
        style={{
          position: 'absolute',
          bottom: 160,
          display: 'flex',
          gap: 20,
        }}
      >
        {['⚡', '🔥', '💪'].map((emoji, i) => {
          const emojiProgress = spring({
            frame: frame - (18 + i * 8),
            fps,
            config: { damping: 10, stiffness: 150 },
          });
          const float = Math.sin(frame * 0.1 + i * 2) * 8;

          return (
            <div
              key={i}
              style={{
                fontSize: 60,
                opacity: emojiProgress,
                transform: `scale(${interpolate(emojiProgress, [0, 1], [0.3, 1])}) translateY(${float}px)`,
              }}
            >
              {emoji}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 完整快速冲击模板
// ============================================
export const XHSFastImpact: React.FC<{
  hookTexts: string[];
  infoItems: Array<{
    number: string;
    title: string;
    content: string;
    color: string;
  }>;
  summary: string;
  ctaText: string;
  sceneTitle?: string;
}> = ({ hookTexts, infoItems, summary, ctaText, sceneTitle }) => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      <DynamicParticleBackground count={60} />

      <Sequence from={0} durationInFrames={fps * 2}>
        <FastHookScene texts={hookTexts} />
      </Sequence>

      <Sequence from={fps * 2} durationInFrames={fps * 15}>
        <FastInfoScene items={infoItems} sceneTitle={sceneTitle} />
      </Sequence>

      <Sequence from={fps * 17} durationInFrames={fps * 3}>
        <FastCTAScene summary={summary} ctaText={ctaText} />
      </Sequence>
    </AbsoluteFill>
  );
};
